import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ComputerIcon from '@mui/icons-material/Computer';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import type { FC, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { wazuhAPI } from 'src/api/mindtime/wazuhAPI';
import { mindtimeWazuhEndpointsApi } from 'src/api/mindtime/wazuhEndpointsApi';
import { Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import {
  getSecurityDeployAgentHref,
  getSecurityEndpointDetailsHref,
} from 'src/mindtime/config/securityNav';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { guardEdrPreviewAction } from 'src/mindtime/utils/edrPreviewGuard';
import { useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type { WazuhAgentDeploymentEligibilityData } from 'src/types/mindtime/wazuhAgents';
import type { Column, TableData } from 'src/types/table';
import logger from 'src/utils/logger';
import {
  EdrDateTime,
  EdrGlobalScopeFilters,
  EdrPageHeader,
  EdrTruncatedText,
  SubscriptionUnavailableMessage,
  formatEndpointLabel,
} from '../shared/edrPrimitives';
import { SecurityToolbarFilter } from '../shared/securityDashboardToolbar';
import { deriveAgentHealthLabel } from '../shared/wazuhDashboardUtils';
import { DeleteAgentsConfirmModal } from './DeleteAgentsConfirmModal';
import { DeployEligibilityModal } from './DeployEligibilityModal';
import { mapWazuhEndpointItem } from './mapWazuhEndpoint';
import type { EndpointRisk, EndpointRow, EndpointStatus } from './types';
import { useEndpointsData, type EndpointsSortParams } from './useEndpointsData';

const DEFAULT_DEPLOY_INELIGIBLE_MESSAGE =
  'You are not eligible for deploying an agent. No licenses remaining.';

const formatDeployEligibilityTooltip = (
  eligibility: WazuhAgentDeploymentEligibilityData | null
): ReactNode => {
  if (!eligibility) return '';
  const licenseCount = eligibility.licenseCount ?? 0;
  const agentCount = eligibility.agentCount ?? 0;
  return (
    <Box sx={{ py: 0.25 }}>
      <Typography sx={{ fontSize: 12, lineHeight: 1.5 }}>{`Licenses: ${licenseCount}`}</Typography>
      <Typography sx={{ fontSize: 12, lineHeight: 1.5 }}>{`Agents Deployed: ${agentCount}`}</Typography>
      <Typography sx={{ fontSize: 12, lineHeight: 1.5 }}>
        {`Agents left: ${licenseCount - agentCount}`}
      </Typography>
    </Box>
  );
};

type EndpointsProps = {
  /** Locks list/summary/endpoint filter to this org and hides All Clients filter. */
  forcedOrganizationId?: string;
  pageTitle?: string;
  breadcrumb?: { links: Array<{ name: string; to: string }> };
  /** Defaults to SECURITY_PAGE_FILTER_KEYS.endpoints */
  filterPageKey?: string;
};

const cardSx = {
  border: '1px solid #E2E8F0',
  borderRadius: '10px',
  boxShadow: 'none',
  backgroundColor: '#FFFFFF',
} as const;

const STATUS_STYLES: Record<EndpointStatus, { label: string; color: string; bg: string }> = {
  active: { label: 'Active', color: '#0D9488', bg: '#CCFBF1' },
  disconnected: { label: 'Disconnected', color: '#B45309', bg: '#FEF3C7' },
  never_connected: { label: 'Never connected', color: '#475569', bg: '#F1F5F9' },
  offline: { label: 'Offline', color: '#DC2626', bg: '#FEE2E2' },
};

const RISK_STYLES: Record<EndpointRisk, { label: string; color: string; bg: string }> = {
  low: { label: 'Low', color: '#16A34A', bg: '#DCFCE7' },
  medium: { label: 'Medium', color: '#CA8A04', bg: '#FEF9C3' },
  high: { label: 'High', color: '#EA580C', bg: '#FFEDD5' },
  critical: { label: 'Critical', color: '#DC2626', bg: '#FEE2E2' },
};

const SYSTEM_OPTIONS = [
  { label: 'Windows', value: 'windows' },
  { label: 'Linux', value: 'linux' },
  { label: 'macOS', value: 'macos' },
];

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Disconnected', value: 'disconnected' },
  { label: 'Never connected', value: 'never_connected' },
];

const Pill: FC<{ label: string; color: string; bg?: string }> = ({ label, color, bg }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: 1,
      py: 0.25,
      borderRadius: '4px',
      fontSize: 11,
      fontWeight: 600,
      color,
      backgroundColor: bg ?? 'transparent',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
);

const StatCard: FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  iconBg: string;
}> = ({ title, value, subtitle, icon, iconBg }) => (
  <Card sx={cardSx} style={{ padding: '16px 18px', height: '100%' }}>
    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
      <Typography style={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>{title}</Typography>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: iconBg,
        }}
      >
        {icon}
      </Box>
    </Box>
    <Typography style={{ fontSize: 28, fontWeight: 700, color: '#1E293B', lineHeight: 1.1 }}>
      {value}
    </Typography>
    {subtitle ? (
      <Typography style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>{subtitle}</Typography>
    ) : null}
  </Card>
);

const Endpoints: FC<EndpointsProps> = ({
  forcedOrganizationId,
  pageTitle = 'Endpoints',
  breadcrumb,
  filterPageKey = SECURITY_PAGE_FILTER_KEYS.endpoints,
}) => {
  const navigate = useNavigate();
  const { canWidget, permissionsResolved } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'endpoints');
  const canTable = canWidget('table');
  const canAgentOverview = canWidget('agentOverview');
  const canDeployAgent = canWidget('deployAgent');
  const showUnavailable = permissionsResolved && !canTable && !canAgentOverview;
  const { selectedClientId } = useWazuhScope();
  const { isPreviewMode } = useEdrDataGate('endpoints');
  const lockedOrganizationId = forcedOrganizationId?.trim() || undefined;

  const { filters, setFilter } =
    useSecurityPageFilters(filterPageKey, { debounceMs: false });
  const {
    system = '',
    status = '',
  } = filters;
  const pageOrganizationId = lockedOrganizationId ?? (selectedClientId || undefined);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sortParams, setSortParams] = useState<EndpointsSortParams | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [restartingId, setRestartingId] = useState<string | null>(null);
  const [checkingDeployEligibility, setCheckingDeployEligibility] = useState(false);
  const [deployEligibility, setDeployEligibility] =
    useState<WazuhAgentDeploymentEligibilityData | null>(null);
  const [deployEligibilityModal, setDeployEligibilityModal] = useState<{
    message: string;
    licenseCount: number;
    agentCount: number;
  } | null>(null);

  useEffect(() => {
    if (!canDeployAgent) {
      setDeployEligibility(null);
      return;
    }

    let cancelled = false;
    wazuhAPI
      .getDeploymentEligibility()
      .then((eligibility) => {
        if (!cancelled) setDeployEligibility(eligibility);
      })
      .catch((error) => {
        if (cancelled) return;
        logger(error, 'error', 'Endpoints');
        setDeployEligibility(null);
      });

    return () => {
      cancelled = true;
    };
  }, [canDeployAgent, refreshKey]);

  const { loading, summary, items: paged } = useEndpointsData(
    page,
    rowsPerPage,
    '',
    {
      pageOrganizationId,
      system,
      status,
    },
    refreshKey,
    sortParams
  );

  const tableRows = useMemo(
    () => (paged?.items ?? []).map(mapWazuhEndpointItem),
    [paged?.items]
  );

  const totalRecords = paged?.totalRecords ?? 0;

  const handleSort = (columnId: string, direction: 'asc' | 'desc' | null) => {
    setSortParams(direction ? { column: columnId, sort: direction } : null);
    setPage(0);
  };

  const openDeleteConfirm = () => {
    if (guardEdrPreviewAction(isPreviewMode)) return;
    if (selectedRows.length === 0) return;
    setDeleteConfirmOpen(true);
  };

  const handleDeleteSelected = async () => {
    if (guardEdrPreviewAction(isPreviewMode)) return;

    const systemWazuhAgentIds = selectedRows.filter(Boolean).join(',');
    if (!systemWazuhAgentIds) return;

    setDeleting(true);
    try {
      await wazuhAPI.deleteAgents({ systemWazuhAgentIds });
      toast.success(
        selectedRows.length === 1
          ? 'Agent deleted'
          : `${selectedRows.length} agents deleted`
      );
      setSelectedRows([]);
      setDeleteConfirmOpen(false);
      setRefreshKey((key) => key + 1);
    } catch (error) {
      logger(error, 'error', 'Endpoints');
      toast.error(error instanceof Error ? error.message : 'Failed to delete agents');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestart = async (systemWazuhAgentId: string) => {
    const id = systemWazuhAgentId.trim();
    if (!id || restartingId) return;

    if (guardEdrPreviewAction(isPreviewMode)) return;

    setRestartingId(id);
    try {
      await mindtimeWazuhEndpointsApi.restart(id);
      toast.success('Agent restart requested');
      setRefreshKey((key) => key + 1);
    } catch (error) {
      logger(error, 'error', 'Endpoints');
      toast.error(error instanceof Error ? error.message : 'Failed to restart agent');
    } finally {
      setRestartingId(null);
    }
  };

  const handleDeployNewAgent = async () => {
    if (guardEdrPreviewAction(isPreviewMode)) return;

    setCheckingDeployEligibility(true);
    try {
      const eligibility = await wazuhAPI.getDeploymentEligibility();
      setDeployEligibility(eligibility);
      if (!eligibility.eligible) {
        setDeployEligibilityModal({
          message: eligibility.message?.trim() || DEFAULT_DEPLOY_INELIGIBLE_MESSAGE,
          licenseCount: eligibility.licenseCount ?? 0,
          agentCount: eligibility.agentCount ?? 0,
        });
        return;
      }
      navigate(getSecurityDeployAgentHref());
    } catch (error) {
      logger(error, 'error', 'Endpoints');
      toast.error(
        error instanceof Error ? error.message : 'Failed to verify deployment eligibility'
      );
    } finally {
      setCheckingDeployEligibility(false);
    }
  };

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'endpoint',
        label: 'Endpoint',
        width: 140,
        sortable: true,
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as EndpointRow).endpoint}
            style={{ fontWeight: 600, color: '#1E293B' }}
            maxWidth={120}
          />
        ),
      },
      {
        id: 'hostName',
        label: 'Endpoint Label',
        width: 140,
        sortable: false,
        render: (row) => (
          <EdrTruncatedText
            value={formatEndpointLabel((row as unknown as EndpointRow).hostName)}
            style={{ color: '#475569' }}
            maxWidth={120}
          />
        ),
      },
      {
        id: 'overallComplianceScore',
        label: 'Compliance Score',
        width: 130,
        align: 'left',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B', textAlign: 'left' }}>
            {`${(row as unknown as EndpointRow).overallComplianceScore ?? 0}%`}
          </Typography>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        width: 130,
        sortable: true,
        render: (row) => {
          const { status: rowStatus } = row as unknown as EndpointRow;
          const { label, color, bg } = STATUS_STYLES[rowStatus] ?? STATUS_STYLES.active;
          return <Pill label={label} color={color} bg={bg} />;
        },
      },
      {
        id: 'risk',
        label: 'Risk',
        width: 90,
        sortable: true,
        render: (row) => {
          const { risk } = row as unknown as EndpointRow;
          const { label, color, bg } = RISK_STYLES[risk];
          return <Pill label={label} color={color} bg={bg} />;
        },
      },
      {
        id: 'os',
        label: 'OS',
        width: 160,
        sortable: true,
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as EndpointRow).os || '—'}
            style={{ color: '#64748B' }}
            maxWidth={140}
          />
        ),
      },
      {
        id: 'vulnerabilities',
        label: 'Open Vulnerabilities',
        width: 150,
        align: 'left',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B', textAlign: 'left' }}>
            {String((row as unknown as EndpointRow).vulnerabilities).padStart(2, '0')}
          </Typography>
        ),
      },
      {
        id: 'criticalVulnerabilities',
        label: 'Critical Vulnerabilities',
        width: 160,
        align: 'left',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B', textAlign: 'left' }}>
            {String((row as unknown as EndpointRow).criticalVulnerabilities).padStart(2, '0')}
          </Typography>
        ),
      },
      {
        id: 'alerts',
        label: 'Alerts',
        width: 80,
        align: 'left',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B', textAlign: 'left' }}>
            {String((row as unknown as EndpointRow).alerts).padStart(2, '0')}
          </Typography>
        ),
      },
      {
        id: 'criticalAlerts',
        label: 'Critical Alerts',
        width: 120,
        align: 'left',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B', textAlign: 'left' }}>
            {String((row as unknown as EndpointRow).criticalAlerts).padStart(2, '0')}
          </Typography>
        ),
      },
      {
        id: 'openIssues',
        label: 'Open Issues',
        width: 100,
        align: 'left',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B', textAlign: 'left' }}>
            {String((row as unknown as EndpointRow).openIssues).padStart(2, '0')}
          </Typography>
        ),
      },
      {
        id: 'lastSeen',
        label: 'Last Seen',
        width: 120,
        align: 'left',
        sortable: true,
        render: (row) => (
          <div style={{ textAlign: 'left' }}>
            <EdrDateTime value={(row as unknown as EndpointRow).lastSeen} />
          </div>
        ),
      },
      {
        id: 'restart',
        label: '',
        width: 72,
        align: 'center',
        render: (row) => {
          if (isPreviewMode) return null;

          const endpointRow = row as unknown as EndpointRow;
          const agentId = endpointRow.id?.trim() ?? '';
          const isRestarting = Boolean(agentId) && restartingId === agentId;
          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              onClick={(event) => event.stopPropagation()}
            >
              <IconButton
                size="small"
                title="Restart agent"
                aria-label="Restart agent"
                disabled={!agentId || Boolean(restartingId)}
                onClick={() => handleRestart(agentId)}
                sx={{ color: '#6366F1', p: 0.5 }}
              >
                {isRestarting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RestartAltIcon style={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [isPreviewMode, restartingId]
  );

  const total = summary?.total ?? 0;
  const healthy = summary?.healthy ?? 0;
  const warning = summary?.warning ?? 0;
  const critical = summary?.critical ?? 0;
  const offline = summary?.offline ?? 0;
  const requiringAttention = summary?.requiringAttention ?? 0;
  const healthyPercent = total > 0 ? Math.round((healthy / total) * 100) : 0;
  const healthLabel = deriveAgentHealthLabel(healthy, warning, offline);

  return (
    <>
      <Helmet>
        <title>{getPageTitle(`Security — ${pageTitle}`)}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ minHeight: '100%', boxSizing: 'border-box', gap: 16 }}
      >
        {breadcrumb ? (
          <EdrPageHeader
            breadcrumb={breadcrumb}
            title={pageTitle}
            subtitle="Endpoints for this organization."
          />
        ) : (
          <Typography
            component="h1"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 22,
              fontWeight: 700,
              color: '#1E293B',
            }}
          >
            {pageTitle}
          </Typography>
        )}

        {showUnavailable ? (
          <SubscriptionUnavailableMessage messageKey="unavailable.endpoints" />
        ) : (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              style={{ gap: 12 }}
            >
              <EdrGlobalScopeFilters
                pageOrganizationId={pageOrganizationId}
                hideOrganization={Boolean(lockedOrganizationId)}
                onChange={() => setPage(0)}
              />
            </Box>

            {canAgentOverview ? (
              <Box
                display="grid"
                style={{
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: 16,
                }}
              >
                <StatCard
                  title="Total Endpoints"
                  value={total}
                  subtitle={healthLabel}
                  icon={<ComputerIcon style={{ fontSize: 18, color: '#3182CE' }} />}
                  iconBg="#DBEAFE"
                />
                <StatCard
                  title="Healthy Endpoints"
                  value={healthy}
                  subtitle={`${healthyPercent}%`}
                  icon={<CheckCircleOutlineIcon style={{ fontSize: 18, color: '#16A34A' }} />}
                  iconBg="#DCFCE7"
                />
                <StatCard
                  title="Endpoints Requiring Attention"
                  value={requiringAttention}
                  subtitle={`${warning} Warning · ${critical} Critical`}
                  icon={<ReportProblemOutlinedIcon style={{ fontSize: 18, color: '#DC2626' }} />}
                  iconBg="#FEE2E2"
                />
                <StatCard
                  title="Offline Endpoints"
                  value={offline}
                  icon={<WifiOffIcon style={{ fontSize: 18, color: '#64748B' }} />}
                  iconBg="#F1F5F9"
                />
              </Box>
            ) : null}
            {canTable ? (
              <Card sx={cardSx} style={{ padding: '16px' }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  style={{ gap: 12, marginBottom: 12 }}
                >
                  <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                    <SecurityToolbarFilter
                      label="System"
                      options={SYSTEM_OPTIONS}
                      value={system}
                      multiple
                      onChange={(value) => {
                        setFilter('system', value);
                        setPage(0);
                      }}
                      minWidth={140}
                    />
                    <SecurityToolbarFilter
                      label="Status"
                      options={STATUS_OPTIONS}
                      value={status}
                      onChange={(value) => {
                        setFilter('status', value);
                        setPage(0);
                      }}
                      minWidth={140}
                    />
                  </Box>

                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    {!isPreviewMode ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        disabled={deleting || selectedRows.length === 0}
                        startIcon={<DeleteOutlineIcon style={{ fontSize: 16 }} />}
                        style={{ textTransform: 'none', fontSize: 13 }}
                        onClick={openDeleteConfirm}
                      >
                        {selectedRows.length > 0
                          ? `Delete (${selectedRows.length})`
                          : 'Delete'}
                      </Button>
                    ) : null}
                    {canDeployAgent && (
                      <Tooltip
                        title={formatDeployEligibilityTooltip(deployEligibility)}
                        placement="top"
                        enterDelay={300}
                      >
                        <span>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={checkingDeployEligibility}
                            startIcon={<AddIcon style={{ fontSize: 16 }} />}
                            style={{ textTransform: 'none', fontSize: 13 }}
                            onClick={handleDeployNewAgent}
                          >
                            {checkingDeployEligibility ? 'Checking…' : 'Deploy New Agent'}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                <Table
                  columns={columns}
                  data={tableRows as unknown as TableData[]}
                  loading={loading}
                  TableContainerProps={{
                    sx: {
                      boxShadow: 'none',
                      borderRadius: 0,
                      overflowX: 'auto',
                    },
                  }}
                  MUITableProps={{
                    sx: {
                      minWidth: 1100,
                      '& th': { whiteSpace: 'nowrap' },
                    },
                  }}
                  showCheckbox
                  selectedRows={selectedRows}
                  onSelectRows={setSelectedRows}
                  onRowClick={(row) => {
                    const endpointRow = row as unknown as EndpointRow;
                    const systemWazuhAgentId = endpointRow.id?.trim();
                    if (!systemWazuhAgentId) return;
                    navigate(getSecurityEndpointDetailsHref(systemWazuhAgentId), {
                      state: { endpointName: endpointRow.endpoint },
                    });
                  }}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  totalRows={totalRecords}
                  onPageChange={setPage}
                  onRowsPerPageChange={(nextRowsPerPage) => {
                    setRowsPerPage(nextRowsPerPage);
                    setPage(0);
                  }}
                  onSort={handleSort}
                  showPagination
                />
              </Card>
            ) : null}
          </>
        )}
      </Box>

      <DeleteAgentsConfirmModal
        open={deleteConfirmOpen}
        count={selectedRows.length}
        loading={deleting}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteSelected}
      />

      <DeployEligibilityModal
        open={deployEligibilityModal != null}
        message={deployEligibilityModal?.message ?? DEFAULT_DEPLOY_INELIGIBLE_MESSAGE}
        licenseCount={deployEligibilityModal?.licenseCount ?? 0}
        agentCount={deployEligibilityModal?.agentCount ?? 0}
        onClose={() => setDeployEligibilityModal(null)}
      />
    </>
  );
};

export default Endpoints;
